import React, { useState, useEffect, useCallback, useRef } from 'react';
import apiCall from '../../utils/api';
import { domainIconMapRaw } from '../Onboarding/domainIconMap'; // Import the full map
import {
  UpdateCardContainer,
  UpdateCard,
  CardHeader,
  TitleWrapper,
  EstablishmentWrapper,
  FormationTitleInput,
  InfoSection,
  InfoGrid,
  InfoItem,
  InfoLabel,
  StyledInput,
  ButtonContainer,
  CancelButton,
  ConfirmButton,
  ErrorMessage,
  SuccessMessage,
  StyledSelect,
  ExtendedTextArea,
  CheckboxGroup,
  CheckboxItem,
  CheckboxLabel,
  CheckboxInput,
  SelectedDomainsDisplay,
  DropdownContent,
  InputWithPercentage
} from './UpdateCardStyles'; // Import from the new styles file

// --- Keep Constants --- 
const formationTypeOptions = [
    "Grande école post bac",
    "BTS",
    "Formations préparatoires à l'enseignement supérieur",
    "BUT",
    "C.M.I",
    "CPGE",
    "DEUST",
    "CS",
    "CUPGE",
    "Licence (L1)"
];

const domainOptions = Object.keys(domainIconMapRaw);
// --- End Constants --- 

const UpdateCardComponent = ({ formation, onCancel }) => {
  const [formData, setFormData] = useState({});
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [selectedPrincipalDomains, setSelectedPrincipalDomains] = useState([]);
  const [selectedSecondaryDomains, setSelectedSecondaryDomains] = useState([]);
  const [isPrincipalDropdownOpen, setIsPrincipalDropdownOpen] = useState(false);
  const [isSecondaryDropdownOpen, setIsSecondaryDropdownOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [loadingEstablishments, setLoadingEstablishments] = useState(false);
  const [establishmentError, setEstablishmentError] = useState(null);
  const [establishments, setEstablishments] = useState([]);

  // --- ADD State for locked fields --- 
  const [isMoyenneGeneralLocked, setIsMoyenneGeneralLocked] = useState(false);
  const [isMoyenneTechnoLocked, setIsMoyenneTechnoLocked] = useState(false);
  const [isMoyenneProLocked, setIsMoyenneProLocked] = useState(false);

  // --- ADD State for Bac % locks --- 
  const [isBacGeneralLocked, setIsBacGeneralLocked] = useState(false);
  const [isBacTechnoLocked, setIsBacTechnoLocked] = useState(false);
  const [isBacProLocked, setIsBacProLocked] = useState(false);
  // ------------------------------------

  const titleTextareaRef = useRef(null);
  const principalDropdownRef = useRef(null);
  const secondaryDropdownRef = useRef(null);

  // Function to adjust textarea height
  const adjustTextareaHeight = (element) => {
    if (element) {
      element.style.height = '1px'; // Temporarily shrink to get accurate scrollHeight
      element.style.height = `${element.scrollHeight}px`; // Set to content height
    }
  };

  // Helper to format number to two decimal places for display
  const formatToTwoDecimals = (value) => {
    if (value === null || value === undefined || value === '') {
      return '';
    }
    const num = parseFloat(value);
    if (isNaN(num)) {
      return ''; // Return empty if not a valid number
    }
    return num.toFixed(2);
  };

  useEffect(() => {
    if (formation) {
        // Helper function to convert domain data to array
        const getDomainsArray = (domainData) => {
            if (Array.isArray(domainData)) {
                return domainData;
            }
            if (typeof domainData === 'string' && domainData.trim() !== '') {
                return domainData.split(',').map(d => d.trim()).filter(Boolean);
            }
            return [];
        };

        // Parse duree (extract number from "X ans" or similar)
        let initialDuree = '';
        if (formation.duree) {
          const match = String(formation.duree).match(/\d+/);
          if (match) {
            initialDuree = match[0]; // Store the number part as a string
          }
        }

        const initialPrincipalDomains = getDomainsArray(formation.domaines_principaux);
        const initialSecondaryDomains = getDomainsArray(formation.domaines_secondaires);

        // --- ADD Check for Moyenne placeholders and determine lock state --- 
        const placeholderStrings = ['PANEL_BAC_NOT_FOUND', 'NO_BADGES_FOUND_or_not_list', null, undefined, ''];
        const checkAndSetLock = (value, setter) => {
            if (placeholderStrings.includes(value)) {
                setter(false); // Unlock if placeholder
                return ''; // Return empty string for display
            } else {
                const num = parseFloat(value);
                if (!isNaN(num)) {
                   setter(true); // Lock if valid number
                   return formatToTwoDecimals(value); // Return formatted number for display
                } else {
                   setter(false); // Unlock if not a valid number (e.g., unexpected string)
                   return ''; // Return empty string for display
                }
            }
        };
        const checkPanel = (value) => placeholderStrings.includes(value) ? '' : value;
        // ------------------------------------------------------------------

        // Helper for Moyenne fields (sets lock state, returns formatted value or '')
        const checkAndSetMoyenneLock = (value, setter) => {
            if (placeholderStrings.includes(value)) {
                setter(false); 
                return ''; 
            } else {
                const num = parseFloat(value);
                if (!isNaN(num)) {
                   setter(true); 
                   return formatToTwoDecimals(value); 
                } else {
                   setter(false); 
                   return ''; 
                }
            }
        };

        // Helper for Bac % fields (sets lock state, returns value or '')
        const checkAndSetBacLock = (value, setter) => {
            if (placeholderStrings.includes(value)) {
                setter(false); 
                return ''; 
            } else {
                // Assume any non-placeholder string is a valid percentage value to lock
                setter(true); 
                return value; // Return the original value (string)
            }
        };

        setFormData({
            formation_nom_long: formation.formation_nom_long || '',
            etablissement_nom: formation.etablissement_nom || '',
            region: formation.region || '',
            departement: formation.departement || '',
            commune: formation.commune || '',
            duree: initialDuree, // Use parsed number string for dropdown selection
            apprentissage: formation.apprentissage || false,
            internat: formation.internat || false,
            prix_normal: formation.prix_normal || '',
            prix_boursier: formation.prix_boursier || '',
            salaire_median: formation.salaire_median || '',
            poursuite_etudes: Array.isArray(formation.poursuite_etudes) ? formation.poursuite_etudes.join(', ') : (formation.poursuite_etudes || ''),
            debouches_professionnels: Array.isArray(formation.debouches_professionnels) ? formation.debouches_professionnels.join(', ') : (formation.debouches_professionnels || ''),
            bac_general: checkAndSetBacLock(formation.bac_general, setIsBacGeneralLocked),
            bac_technologique: checkAndSetBacLock(formation.bac_technologique, setIsBacTechnoLocked),
            bac_professionnel: checkAndSetBacLock(formation.bac_professionnel, setIsBacProLocked),
            moyenne_formation_general: checkAndSetMoyenneLock(formation.moyenne_formation_general, setIsMoyenneGeneralLocked),
            moyenne_formation_technologique: checkAndSetMoyenneLock(formation.moyenne_formation_technologique, setIsMoyenneTechnoLocked),
            moyenne_formation_professionnel: checkAndSetMoyenneLock(formation.moyenne_formation_professionnel, setIsMoyenneProLocked),
            link: formation.link || '',
            site_web: formation.site_web || '',
        });

        setSelectedPrincipalDomains(initialPrincipalDomains);
        setSelectedSecondaryDomains(initialSecondaryDomains);

        // Initialize selectedTypes from type_final field
        const initialTypes = formation.type_final
            ? formation.type_final.split(',').map(t => t.trim()).filter(t => formationTypeOptions.includes(t))
            : [];
        setSelectedTypes(initialTypes);

        setError(null);
        setSuccess(null);

        // Adjust height initially after data is loaded
        setTimeout(() => adjustTextareaHeight(titleTextareaRef.current), 0);
    }
  }, [formation]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (principalDropdownRef.current && !principalDropdownRef.current.contains(event.target)) {
        setIsPrincipalDropdownOpen(false);
      }
      if (secondaryDropdownRef.current && !secondaryDropdownRef.current.contains(event.target)) {
        setIsSecondaryDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (type !== 'checkbox') {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));

      // Adjust height specifically for title textarea on change
      if (name === 'formation_nom_long' && titleTextareaRef.current) {
        adjustTextareaHeight(titleTextareaRef.current);
      }
    }
  };

  // Handler for the formation type checkboxes
  const handleTypeCheckboxChange = (e) => {
      const { value, checked } = e.target;
      setSelectedTypes(prevSelectedTypes => {
          if (checked) {
              // Add type if checked and not already present
              return [...prevSelectedTypes, value];
          } else {
              // Remove type if unchecked
              return prevSelectedTypes.filter(type => type !== value);
          }
      });
  };

  const togglePrincipalDropdown = () => setIsPrincipalDropdownOpen(!isPrincipalDropdownOpen);
  const toggleSecondaryDropdown = () => setIsSecondaryDropdownOpen(!isSecondaryDropdownOpen);

  // Handler for the domain checkboxes (Principal)
  const handlePrincipalDomainChange = useCallback((e) => {
      const { value, checked } = e.target;
      setSelectedPrincipalDomains(prev =>
          checked ? [...prev, value] : prev.filter(domain => domain !== value)
      );
  }, []);

  // Handler for the domain checkboxes (Secondary)
  const handleSecondaryDomainChange = useCallback((e) => {
      const { value, checked } = e.target;
      setSelectedSecondaryDomains(prev =>
          checked ? [...prev, value] : prev.filter(domain => domain !== value)
      );
  }, []);

  // Helper function to display selected domains
  const formatSelectedDomains = (selectedDomains) => {
    const uniqueDomains = [...new Set(selectedDomains)]; // Get unique domains
    const count = uniqueDomains.length;

    if (count === 0) {
      return "Sélectionner..."; // Placeholder text
    }
    if (count <= 2) {
      return uniqueDomains.join(', '); // Join unique names
    }
    return `${count} domaines sélectionnés`; // Use unique count
  };

  const handleSave = async () => {
    if (!formation || !formation.id) {
      setError("Formation ID is missing. Cannot save.");
      return;
    }

    setIsSaving(true);
    setError(null);
    setSuccess(null);

    const typeFinalString = selectedTypes.join(', ');

    // Function to safely parse and round to 2 decimal places
    const parseFloatAndRound = (value) => {
        if (value === null || value === undefined || value === '') {
            return null; // Keep null/empty as is
        }
        const num = parseFloat(value);
        if (isNaN(num)) {
            return null; // Handle cases where parsing fails
        }
        // Round to 2 decimal places
        return Math.round(num * 100) / 100;
    };

    // Format duree back to "X ans" string for saving
    const formattedDuree = formData.duree ? `${formData.duree} ans` : null;

    const dataToSend = {
      ...formData,
      type_final: typeFinalString,
      domaines_principaux: selectedPrincipalDomains,
      domaines_secondaires: selectedSecondaryDomains,
      duree: formattedDuree,
      poursuite_etudes: typeof formData.poursuite_etudes === 'string' ? formData.poursuite_etudes.split(/\s*,\s*/).map(s => s.trim()).filter(Boolean) : [],
      debouches_professionnels: typeof formData.debouches_professionnels === 'string' ? formData.debouches_professionnels.split(/\s*,\s*/).map(s => s.trim()).filter(Boolean) : [],
      prix_normal: formData.prix_normal ? parseFloat(formData.prix_normal) : null,
      prix_boursier: formData.prix_boursier ? parseFloat(formData.prix_boursier) : null,
      salaire_median: formData.salaire_median ? parseFloat(formData.salaire_median) : null,
      moyenne_formation_general: parseFloatAndRound(formData.moyenne_formation_general),
      moyenne_formation_technologique: parseFloatAndRound(formData.moyenne_formation_technologique),
      moyenne_formation_professionnel: parseFloatAndRound(formData.moyenne_formation_professionnel),
    };

    // Remove fields that shouldn't be sent or might cause issues if empty string vs null
    Object.keys(dataToSend).forEach(key => {
        if (dataToSend[key] === '') {
           // Adjust this list if other fields need specific null handling for empty strings
           if ([ 'prix_normal', 'prix_boursier', 'salaire_median' /* Moyenne fields handled by parseFloatAndRound */].includes(key)) {
               dataToSend[key] = null;
           }
        }
    });

    console.log("Data being sent (with comma split):", dataToSend);

    try {
      const response = await apiCall(
        `${process.env.REACT_APP_BACKEND_URL}/manager/formations/${formation.id}/`,
        'PUT',
        dataToSend
      );
      setSuccess("Formation mise à jour avec succès!");
      setTimeout(() => {
        setSuccess(null);
        onCancel();
      }, 1000);
    } catch (err) {
      console.error('Error updating formation:', err);
      const errorMsg = err.response?.data?.detail || err.response?.data?.error || 'Failed to update formation. Please check the details and try again.';
      setError(errorMsg);
    } finally {
      setIsSaving(false);
    }
  };

  useEffect(() => {
    const fetchEstablishments = async () => {
      setLoadingEstablishments(true);
      setEstablishmentError(null);
      try {
        const managerDataString = localStorage.getItem('managerData');
        
        // Always fetch from API
        console.log("Fetching establishments from API");
        const data = await apiCall(
          `${process.env.REACT_APP_BACKEND_URL}/manager/my-establishments/`,
          'GET'
        );
        
        console.log("Establishments from API:", data);
        
        // Update localStorage with fresh data
        if (managerDataString) {
          const manager = JSON.parse(managerDataString);
          manager.establishments = data || [];
          localStorage.setItem('managerData', JSON.stringify(manager));
        } else {
          // If no managerData exists yet, create it
          localStorage.setItem('managerData', JSON.stringify({
            establishments: data || []
          }));
        }
        
        setEstablishments(data || []);
        
        // If we have a current establishment in formData, make sure it's selected
        if (formData.etablissement_nom && data.some(e => e.name === formData.etablissement_nom)) {
          // Establishment is already selected, no need to change
        } else if (data.length === 1) {
          // If there's only one establishment, select it by default
          setFormData(prev => ({
            ...prev,
            etablissement_nom: data[0].name
          }));
        }
      } catch (err) {
        console.error('Error fetching establishments:', err);
        setEstablishmentError('Impossible de charger la liste des établissements.');
        setEstablishments([]);
      } finally {
        setLoadingEstablishments(false);
      }
    };

    fetchEstablishments();
  }, [formation]);

  if (!formation || !formData.formation_nom_long) {
    return null;
  }

  return (
    <UpdateCardContainer>
      <UpdateCard>
        <CardHeader>
          <TitleWrapper>
            <InfoLabel htmlFor="formation_nom_long">Nom de la formation</InfoLabel>
            <FormationTitleInput
              ref={titleTextareaRef}
              rows="1"
              id="formation_nom_long"
              name="formation_nom_long"
              value={formData.formation_nom_long}
              onChange={handleInputChange}
              placeholder="Nom complet de la formation"
            />
          </TitleWrapper>
          <EstablishmentWrapper>
            <InfoLabel htmlFor="etablissement_nom">Établissement</InfoLabel>
            {loadingEstablishments ? (
              <StyledSelect disabled>
                <option>Chargement des établissements...</option>
              </StyledSelect>
            ) : establishmentError ? (
              <StyledSelect disabled>
                <option>Erreur de chargement</option>
              </StyledSelect>
            ) : (
              <StyledSelect
                id="etablissement_nom"
                name="etablissement_nom"
                value={formData.etablissement_nom}
                onChange={handleInputChange}
              >
                <option value="">Sélectionner un établissement</option>
                {establishments && establishments.length > 0 ? (
                  establishments.map((establishment) => (
                    <option key={establishment.uai} value={establishment.name}>
                      {establishment.name} ({establishment.uai})
                    </option>
                  ))
                ) : (
                  <option value="" disabled>Aucun établissement disponible</option>
                )}
              </StyledSelect>
            )}
          </EstablishmentWrapper>
        </CardHeader>
        
        <InfoSection>
          <InfoGrid>
            <InfoItem>
              <InfoLabel>Région</InfoLabel>
              <StyledInput name="region" value={formData.region} onChange={handleInputChange} />
            </InfoItem>
            <InfoItem>
              <InfoLabel>Département</InfoLabel>
              <StyledInput name="departement" value={formData.departement} onChange={handleInputChange} />
            </InfoItem>
            <InfoItem>
              <InfoLabel>Commune</InfoLabel>
              <StyledInput name="commune" value={formData.commune} onChange={handleInputChange} />
            </InfoItem>
          </InfoGrid>
        </InfoSection>
        
        <InfoSection>
          <InfoGrid gridTemplateColumns="repeat(auto-fit, minmax(150px, 1fr))">
            <InfoItem style={{ gridColumn: '1 / -1' }}>
              <InfoLabel>Type de formation</InfoLabel>
              <CheckboxGroup>
                {formationTypeOptions.map(typeOption => (
                  <CheckboxItem key={typeOption}>
                    <CheckboxInput
                      id={`type-${typeOption}`}
                      value={typeOption}
                      checked={selectedTypes.includes(typeOption)}
                      onChange={handleTypeCheckboxChange}
                    />
                    <CheckboxLabel htmlFor={`type-${typeOption}`}>{typeOption}</CheckboxLabel>
                  </CheckboxItem>
                ))}
              </CheckboxGroup>
            </InfoItem>
            <InfoItem>
              <InfoLabel htmlFor="duree">Durée (années)</InfoLabel>
              <StyledSelect id="duree" name="duree" value={formData.duree} onChange={handleInputChange}>
                <option value="">Choisir...</option>
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </StyledSelect>
            </InfoItem>
            <InfoItem>
              <InfoLabel htmlFor="apprentissage">Alternance</InfoLabel>
              <StyledSelect id="apprentissage" name="apprentissage" value={formData.apprentissage.toString()} onChange={handleInputChange}>
                <option value="true">Oui</option>
                <option value="false">Non</option>
              </StyledSelect>
            </InfoItem>
            <InfoItem>
              <InfoLabel htmlFor="internat">Internat</InfoLabel>
              <StyledSelect id="internat" name="internat" value={formData.internat.toString()} onChange={handleInputChange}>
                <option value="true">Oui</option>
                <option value="false">Non</option>
              </StyledSelect>
            </InfoItem>
          </InfoGrid>
        </InfoSection>
        
        <InfoSection>
          <InfoGrid gridTemplateColumns="repeat(auto-fit, minmax(180px, 1fr))">
            <InfoItem>
              <InfoLabel>Prix Normal (€)</InfoLabel>
              <StyledInput type="number" name="prix_normal" value={formData.prix_normal} onChange={handleInputChange} placeholder="e.g., 1500.00"/>
            </InfoItem>
            <InfoItem>
              <InfoLabel>Prix Boursier (€)</InfoLabel>
              <StyledInput type="number" name="prix_boursier" value={formData.prix_boursier} onChange={handleInputChange} placeholder="e.g., 0.00"/>
            </InfoItem>
            <InfoItem>
              <InfoLabel>Salaire médian de sortie (€)</InfoLabel>
              <StyledInput type="number" name="salaire_median" value={formData.salaire_median} onChange={handleInputChange} placeholder="e.g., 35000"/>
            </InfoItem>
          </InfoGrid>
        </InfoSection>
        
        <InfoSection>
          <InfoGrid gridTemplateColumns="1fr 1fr">
            <InfoItem ref={principalDropdownRef} style={{ position: 'relative' }}>
              <InfoLabel>Domaines Principaux</InfoLabel>
              <SelectedDomainsDisplay onClick={togglePrincipalDropdown}>
                {formatSelectedDomains(selectedPrincipalDomains)}
              </SelectedDomainsDisplay>
              {isPrincipalDropdownOpen && (
                <DropdownContent>
                  <CheckboxGroup>
                    {[...domainOptions]
                      .sort((a, b) => {
                          const aSelected = selectedPrincipalDomains.includes(a);
                          const bSelected = selectedPrincipalDomains.includes(b);
                          if (aSelected && !bSelected) return -1; // a comes first if selected
                          if (!aSelected && bSelected) return 1;  // b comes first if selected
                          return a.localeCompare(b); // Otherwise, sort alphabetically
                      })
                      .map(domainOption => (
                      <CheckboxItem key={`principal-${domainOption}`}>
                        <CheckboxInput
                          id={`principal-${domainOption}`}
                          value={domainOption}
                          checked={selectedPrincipalDomains.includes(domainOption)}
                          onChange={handlePrincipalDomainChange}
                        />
                        <CheckboxLabel htmlFor={`principal-${domainOption}`}>{domainOption}</CheckboxLabel>
                      </CheckboxItem>
                    ))}
                  </CheckboxGroup>
                </DropdownContent>
              )}
            </InfoItem>
            <InfoItem ref={secondaryDropdownRef} style={{ position: 'relative' }}>
              <InfoLabel>Domaines Secondaires</InfoLabel>
              <SelectedDomainsDisplay onClick={toggleSecondaryDropdown}>
                {formatSelectedDomains(selectedSecondaryDomains)}
              </SelectedDomainsDisplay>
              {isSecondaryDropdownOpen && (
                <DropdownContent>
                  <CheckboxGroup>
                    {[...domainOptions]
                      .sort((a, b) => {
                          const aSelected = selectedSecondaryDomains.includes(a);
                          const bSelected = selectedSecondaryDomains.includes(b);
                          if (aSelected && !bSelected) return -1; // a comes first if selected
                          if (!aSelected && bSelected) return 1;  // b comes first if selected
                          return a.localeCompare(b); // Otherwise, sort alphabetically
                      })
                      .map(domainOption => (
                      <CheckboxItem key={`secondary-${domainOption}`}>
                        <CheckboxInput
                          id={`secondary-${domainOption}`}
                          value={domainOption}
                          checked={selectedSecondaryDomains.includes(domainOption)}
                          onChange={handleSecondaryDomainChange}
                        />
                        <CheckboxLabel htmlFor={`secondary-${domainOption}`}>{domainOption}</CheckboxLabel>
                      </CheckboxItem>
                    ))}
                  </CheckboxGroup>
                </DropdownContent>
              )}
            </InfoItem>
          </InfoGrid>
        </InfoSection>
        
        <InfoSection>
          <InfoGrid gridTemplateColumns="1fr 1fr">
            <InfoItem>
              <InfoLabel>Poursuite d'études</InfoLabel>
              <ExtendedTextArea name="poursuite_etudes" value={formData.poursuite_etudes} onChange={handleInputChange} placeholder="e.g., Master Informatique\nÉcole d'ingénieur"/>
            </InfoItem>
            <InfoItem>
              <InfoLabel>Débouchés Professionnels</InfoLabel>
              <ExtendedTextArea name="debouches_professionnels" value={formData.debouches_professionnels} onChange={handleInputChange} placeholder="e.g., Développeur Web\nChef de projet"/>
            </InfoItem>
          </InfoGrid>
        </InfoSection>
        
        <InfoSection>
          <InfoGrid>
            <InfoItem>
              <InfoLabel htmlFor="bac_general">
                 Taux de Bac Général {isBacGeneralLocked && <span style={{ color: 'currentColor' }}>🔒</span>}
              </InfoLabel>
              <InputWithPercentage>
                <StyledInput 
                  type="number" 
                  step="0.01" 
                  name="bac_general" 
                  value={formData.bac_general} 
                  onChange={handleInputChange} 
                  placeholder="e.g., 95.5" 
                  disabled={isBacGeneralLocked}
                />
                <span>%</span>
              </InputWithPercentage>
            </InfoItem>
            <InfoItem>
              <InfoLabel htmlFor="bac_technologique">
                 Taux de Bac Technologique {isBacTechnoLocked && <span style={{ color: 'currentColor' }}>🔒</span>}
              </InfoLabel>
              <InputWithPercentage>
                <StyledInput 
                  type="number" 
                  step="0.01" 
                  name="bac_technologique" 
                  value={formData.bac_technologique} 
                  onChange={handleInputChange} 
                  placeholder="e.g., 80.0" 
                  disabled={isBacTechnoLocked}
                  />
                <span>%</span>
              </InputWithPercentage>
            </InfoItem>
            <InfoItem>
              <InfoLabel htmlFor="bac_professionnel">
                 Taux de Bac Professionnel {isBacProLocked && <span style={{ color: 'currentColor' }}>🔒</span>}
              </InfoLabel>
              <InputWithPercentage>
                <StyledInput 
                  type="number" 
                  step="0.01" 
                  name="bac_professionnel" 
                  value={formData.bac_professionnel} 
                  onChange={handleInputChange} 
                  placeholder="e.g., 75.25" 
                  disabled={isBacProLocked}
                />
                <span>%</span>
              </InputWithPercentage>
            </InfoItem>
          </InfoGrid>
        </InfoSection>
        
        <InfoSection>
          <InfoGrid>
            <InfoItem>
              <InfoLabel htmlFor="moyenne_formation_general">
                 Moyenne Générale (Bac Général) {isMoyenneGeneralLocked && '🔒'}
              </InfoLabel>
              <StyledInput 
                type="number" 
                step="0.01" 
                name="moyenne_formation_general" 
                value={formData.moyenne_formation_general} 
                onChange={handleInputChange} 
                placeholder="e.g., 14.50"
                disabled={isMoyenneGeneralLocked}
              />
            </InfoItem>
            <InfoItem>
              <InfoLabel htmlFor="moyenne_formation_technologique">
                 Moyenne Générale (Bac Techno) {isMoyenneTechnoLocked && '🔒'}
              </InfoLabel>
              <StyledInput 
                type="number" 
                step="0.01" 
                name="moyenne_formation_technologique" 
                value={formData.moyenne_formation_technologique} 
                onChange={handleInputChange} 
                placeholder="e.g., 13.00"
                disabled={isMoyenneTechnoLocked}
              />
            </InfoItem>
            <InfoItem>
              <InfoLabel htmlFor="moyenne_formation_professionnel">
                 Moyenne Générale (Bac Pro) {isMoyenneProLocked && '🔒'}
              </InfoLabel>
              <StyledInput 
                type="number" 
                step="0.01" 
                name="moyenne_formation_professionnel" 
                value={formData.moyenne_formation_professionnel} 
                onChange={handleInputChange} 
                placeholder="e.g., 12.85"
                disabled={isMoyenneProLocked}
               />
            </InfoItem>
          </InfoGrid>
        </InfoSection>
        
        <InfoSection>
          <InfoGrid>
            <InfoItem>
              <InfoLabel>Lien Formation (Parcoursup, etc.)</InfoLabel>
              <StyledInput type="url" name="link" value={formData.link} onChange={handleInputChange} />
            </InfoItem>
            <InfoItem>
              <InfoLabel>Site Web Établissement</InfoLabel>
              <StyledInput type="url" name="site_web" value={formData.site_web} onChange={handleInputChange} />
            </InfoItem>
          </InfoGrid>
        </InfoSection>
        
        {error && <ErrorMessage>{error}</ErrorMessage>}
        {success && <SuccessMessage>{success}</SuccessMessage>}
        
        <ButtonContainer>
          <CancelButton onClick={onCancel} disabled={isSaving}>
            Annuler
          </CancelButton>
          <ConfirmButton onClick={handleSave} disabled={isSaving}>
            {isSaving ? 'Enregistrement...' : 'Confirmer'}
          </ConfirmButton>
        </ButtonContainer>
      </UpdateCard>
    </UpdateCardContainer>
  );
};

export default UpdateCardComponent; 